/*
 * File: StudentInfo.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useDialogs } from '@toolpad/core/useDialogs';
import moment from 'moment';
import { useNavigate } from 'react-router';

import type { GetStudentResponse } from 'types/students';

import UpdateStudentPermitDialog from 'components/Dialogs/Students/UpdateStudentPermitDialog';
import RequireRole from 'components/RequireRole';

import { formatCents } from 'utils/formatCurrency';

import { useDriveOverviewDialogContext } from '../../context';
import { formatDLN, getPermitStatus } from '../../utils';

interface StudentInfoProps {
  student: GetStudentResponse;
  balanceStatus: {
    amount: number;
    isOverdue: boolean;
  };
}

export default function StudentInfo({ student, balanceStatus }: StudentInfoProps) {
  const navigate = useNavigate();
  const dialogs = useDialogs();
  const { onClose } = useDriveOverviewDialogContext();

  const handleUpdatePermit = () => {
    dialogs.open(UpdateStudentPermitDialog, { id: student.id });
  };
  // Permit status calculation
  const permitExpiration = student?.student?.permit_expiration ? moment(student.student.permit_expiration) : null;
  const permitStatus = getPermitStatus(permitExpiration);

  return (
    <Stack gap={4}>
      {/* Permit Status */}
      {student?.student?.permit_num ? (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <BadgeIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="medium">
              DLN: {formatDLN(student.student.permit_num)}
            </Typography>
            <Tooltip title="Update permit information">
              <IconButton size="small" onClick={handleUpdatePermit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {permitStatus.status !== 'valid' && (
              <Chip
                icon={<WarningIcon />}
                label={permitStatus.status === 'expired' ? 'Expired' : 'Expiring Soon'}
                size="small"
                color={permitStatus.status === 'expired' ? 'error' : 'warning'}
                sx={{ height: 20 }}
              />
            )}
          </Box>
          {permitExpiration && (
            <Typography variant="caption" color="text.secondary" pl={3.5}>
              Expires {permitExpiration.format('M/D/YYYY')} ({permitExpiration.fromNow()})
            </Typography>
          )}
        </Box>
      ) : (
        <Alert
          severity="error"
          sx={{ py: 0.5, width: 'fit-content' }}
          action={
            <Button color="inherit" size="small" onClick={handleUpdatePermit}>
              Add Permit Info
            </Button>
          }
        >
          No permit on file
        </Alert>
      )}

      {/* Conditional Alert for balance due */}
      {balanceStatus.amount > 0 && (
        <Alert
          severity={balanceStatus.isOverdue ? 'error' : 'info'}
          sx={{ py: 0.5 }}
          action={
            <RequireRole role="ADMIN">
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  navigate(`/admin/students/${student.id}?tab=ledger`);
                  onClose();
                }}
              >
                View Charges Due
              </Button>
            </RequireRole>
          }
        >
          Student has {formatCents(balanceStatus.amount)} balance due.
        </Alert>
      )}
    </Stack>
  );
}
